import * as Popover from '@rn-primitives/popover';
import { Plus } from 'lucide-react-native';
import { useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from 'react-native-reanimated';

import { Icon } from '@/components/ui/Icon';

import { MenuItem } from './MenuItem';

export interface NewActionFabProps {
  /** Open the pick-a-template "Start a run" sheet. */
  onStartRun: () => void;
  /** Open the new-template sheet (or route to paywall when at the limit). */
  onNewTemplate: () => void;
}

/**
 * Floating "+" button pinned to the bottom-right of the Home screen. Opens a
 * popover with the two creation actions. The popover mirrors {@link AccountMenu}
 * to reuse the same web/native portal handling (single Portal child, sibling
 * backdrop, no `asChild` on Content, fade/slide on an inner Animated.View).
 */
export function NewActionFab({ onStartRun, onNewTemplate }: NewActionFabProps) {
  const triggerRef = useRef<Popover.TriggerRef>(null);

  // Close the popover before running the action (matches AccountMenu).
  const handle = (fn: () => void) => () => {
    triggerRef.current?.close();
    fn();
  };

  return (
    <Popover.Root>
      <Popover.Trigger ref={triggerRef} asChild>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Create"
          className="h-16 w-16 items-center justify-center rounded-full bg-signal shadow-lg active:scale-95"
        >
          <Icon icon={Plus} size={30} colorVar="--color-on-signal" strokeWidth={2.5} />
        </Pressable>
      </Popover.Trigger>
      {/* Portal accepts a SINGLE child on web (Radix): the dim backdrop and the
          anchored Content are SIBLINGS under one wrapper. Content paints on top,
          so backdrop taps close while taps on the menu hit the menu. */}
      <Popover.Portal>
        <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
          <Animated.View
            entering={FadeIn.duration(110)}
            exiting={FadeOut.duration(90)}
            className="absolute inset-0"
          >
            <Pressable
              className="flex-1 bg-black/20"
              onPress={() => triggerRef.current?.close()}
            />
          </Animated.View>
          {/* No `asChild`: Content injects `opacity: 0` for the first (pre-measure)
              frame; letting it render its own wrapper absorbs that so it doesn't
              collide with the inner Animated.View's fade. */}
          <Popover.Content side="top" align="center" sideOffset={8}>
            <Animated.View
              entering={FadeInDown.duration(110)}
              exiting={FadeOutDown.duration(90)}
              className="w-56 rounded-2xl border border-border bg-background p-2 shadow-lg"
            >
              <MenuItem label="Start a run" onPress={handle(onStartRun)} />
              <MenuItem label="New template" onPress={handle(onNewTemplate)} />
            </Animated.View>
          </Popover.Content>
        </View>
      </Popover.Portal>
    </Popover.Root>
  );
}
